import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Message, Placeholder } from 'rsuite';
import { showToast } from '../../utils/toast';
import {
  ALLOWED_ATTACHMENT_SUMMARY,
  ATTACHMENT_ACCEPT,
  formatAttachmentDateTime,
  formatBytes,
  getFriendlyDeleteError,
  getFriendlyUploadError,
  partitionAllowedFiles,
  triggerBlobDownload,
  triggerBlobPreview,
  unsupportedFilesMessage,
} from '../../utils/expenseAttachments';
import {
  useDeleteExpenseAttachment,
  useExpenseAttachmentContent,
  useExpenseAttachments,
  useUploadExpenseAttachment,
} from '../../hooks/useQueries';

// Manages the files already stored against an existing expense. Unlike the
// create form — where attachments are held locally and uploaded once the
// expense exists — every action here hits the API immediately, so there is
// nothing to save afterwards.
export default function ExpenseAttachmentsPanel({
  expenseId,
  isMobile = false,
  disabled = false,
  allowUpload = true,
  allowDelete = true,
  // Optional extra controls under each file. The edit form uses it to ask the
  // same "is this a receipt?" question the new expense form asks; the panel
  // stays unaware of what's being rendered.
  renderRowFooter,
}) {
  const attachmentsQuery = useExpenseAttachments(expenseId, { enabled: Boolean(expenseId) });
  const uploadMutation = useUploadExpenseAttachment();
  const contentMutation = useExpenseAttachmentContent();
  const deleteMutation = useDeleteExpenseAttachment();

  const [isDragOverUpload, setIsDragOverUpload] = useState(false);
  const [busyAttachmentId, setBusyAttachmentId] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const rows = useMemo(() => attachmentsQuery.data || [], [attachmentsQuery.data]);
  const isBusy = disabled || uploadMutation.isPending;

  useEffect(() => {
    if (!uploadMutation.error) return;
    showToast('error', getFriendlyUploadError(uploadMutation.error), {
      header: 'Upload failed',
      duration: 7000,
    });
  }, [uploadMutation.error]);

  useEffect(() => {
    if (!deleteMutation.error) return;
    showToast('error', getFriendlyDeleteError(deleteMutation.error), {
      header: 'Delete failed',
      duration: 7000,
    });
  }, [deleteMutation.error]);

  const uploadFiles = useCallback(async (fileList) => {
    // A drop can mix supported and unsupported files; upload the good ones and
    // report the rest rather than discarding the whole batch.
    const { allowed: files, rejected } = partitionAllowedFiles(fileList);
    if (rejected.length > 0) {
      showToast('error', unsupportedFilesMessage(rejected), {
        header: 'Unsupported file type',
        duration: 7000,
      });
    }
    if (!expenseId || files.length === 0) return;

    // Sequential so a failure stops the run instead of firing the same error
    // once per remaining file.
    for (const file of files) {
      try {
        await uploadMutation.mutateAsync({ expenseId, file });
      } catch {
        return;
      }
    }
  }, [expenseId, uploadMutation]);

  const openFilePicker = useCallback(() => {
    if (isBusy) return;
    fileInputRef.current?.click();
  }, [isBusy]);

  const openCameraPicker = useCallback(() => {
    if (isBusy) return;
    cameraInputRef.current?.click();
  }, [isBusy]);

  const onUploadInputChange = useCallback((event) => {
    // `event.target.files` is a live FileList, not a snapshot: clearing the
    // input's value (which is what lets the same file be picked twice in a row)
    // empties the list that was just read from it. Copy the files out first, or
    // the upload runs against nothing and silently does nothing at all.
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    uploadFiles(files);
  }, [uploadFiles]);

  const onUploadDragOver = useCallback((event) => {
    event.preventDefault();
    if (isBusy) return;
    setIsDragOverUpload(true);
  }, [isBusy]);

  const onUploadDragLeave = useCallback((event) => {
    event.preventDefault();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragOverUpload(false);
    }
  }, []);

  const onUploadDrop = useCallback((event) => {
    event.preventDefault();
    setIsDragOverUpload(false);
    if (isBusy) return;
    uploadFiles(event.dataTransfer?.files);
  }, [isBusy, uploadFiles]);

  const handlePreview = async (row) => {
    if (!expenseId || !row?.id) return;
    setBusyAttachmentId(row.id);
    try {
      const result = await contentMutation.mutateAsync({ expenseId, attachmentId: row.id, download: false });
      triggerBlobPreview(result.blob);
    } catch {
      // Surfaced by the inline message below.
    } finally {
      setBusyAttachmentId(null);
    }
  };

  const handleDownload = async (row) => {
    if (!expenseId || !row?.id) return;
    setBusyAttachmentId(row.id);
    try {
      const result = await contentMutation.mutateAsync({ expenseId, attachmentId: row.id, download: true });
      triggerBlobDownload(result.blob, row.file_name);
    } catch {
      // Surfaced by the inline message below.
    } finally {
      setBusyAttachmentId(null);
    }
  };

  const handleDelete = async (row) => {
    if (!expenseId || !row?.id) return;
    const ok = window.confirm(`Delete attachment "${row.file_name || 'file'}"? This will also delete it from SharePoint.`);
    if (!ok) return;

    setBusyAttachmentId(row.id);
    try {
      await deleteMutation.mutateAsync({ expenseId, attachmentId: row.id });
    } catch {
      // Surfaced by the toast above.
    } finally {
      setBusyAttachmentId(null);
    }
  };

  return (
    <div className="d-flex flex-column gap-2">
      {attachmentsQuery.isLoading ? <Placeholder.Paragraph rows={3} active /> : null}

      {attachmentsQuery.error ? (
        <Message type="error" showIcon>
          {String(attachmentsQuery.error?.message || attachmentsQuery.error)}
        </Message>
      ) : null}

      {contentMutation.error ? (
        <Message type="error" showIcon header="File access failed">
          {String(contentMutation.error?.message || contentMutation.error)}
        </Message>
      ) : null}

      {!attachmentsQuery.isLoading ? (
        rows.length === 0 ? (
          <div className="text-muted" style={{ fontSize: 13 }}>No attachments yet.</div>
        ) : (
          rows.map((row) => (
            <div
              key={row.id}
              style={{
                border: '1px solid var(--rs-border-secondary)',
                borderRadius: 8,
                padding: '8px 10px',
                background: 'var(--rs-bg-card)',
              }}
            >
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div style={{ minWidth: 0 }}>
                  <div className="text-truncate" style={{ fontWeight: 600 }} title={row.file_name}>
                    {row.file_name}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {formatBytes(row.file_size_bytes)}
                    {row.uploader_name ? ` • Uploaded by ${row.uploader_name}` : ''}
                    {row.created_at ? ` • ${formatAttachmentDateTime(row.created_at)}` : ''}
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    size="xs"
                    appearance="ghost"
                    loading={contentMutation.isPending && busyAttachmentId === row.id}
                    onClick={() => handlePreview(row)}
                  >
                    Preview
                  </Button>
                  <Button
                    size="xs"
                    appearance="primary"
                    loading={contentMutation.isPending && busyAttachmentId === row.id}
                    onClick={() => handleDownload(row)}
                  >
                    Download
                  </Button>
                  {allowDelete ? (
                    <Button
                      size="xs"
                      appearance="ghost"
                      color="red"
                      disabled={disabled}
                      loading={deleteMutation.isPending && busyAttachmentId === row.id}
                      onClick={() => handleDelete(row)}
                    >
                      Delete
                    </Button>
                  ) : null}
                </div>
              </div>

              {renderRowFooter ? (
                <div className="mt-2">{renderRowFooter(row)}</div>
              ) : null}
            </div>
          ))
        )
      ) : null}

      {allowUpload ? (
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload attachments"
          className="d-flex flex-column align-items-center justify-content-center text-center mt-1"
          onClick={openFilePicker}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openFilePicker();
            }
          }}
          onDragEnter={onUploadDragOver}
          onDragOver={onUploadDragOver}
          onDragLeave={onUploadDragLeave}
          onDrop={onUploadDrop}
          style={{
            minHeight: 120,
            borderRadius: 10,
            border: `2px dashed ${isDragOverUpload ? 'var(--bdc-primary)' : 'var(--rs-border-primary)'}`,
            background: isDragOverUpload ? 'var(--bdc-focus-ring)' : 'transparent',
            cursor: isBusy ? 'not-allowed' : 'pointer',
            padding: 16,
          }}
        >
          <i
            className="bi bi-cloud-arrow-up"
            aria-hidden="true"
            style={{ fontSize: 24, marginBottom: 6, color: 'var(--bdc-primary)' }}
          />
          <div style={{ fontWeight: 600 }}>
            {isMobile ? 'Tap below to add files or take a photo' : 'Drag and drop files here'}
          </div>
          <div className="text-muted" style={{ fontSize: 13, marginBottom: 10 }}>
            Accepted: {ALLOWED_ATTACHMENT_SUMMARY}.
          </div>
          <div className="d-flex gap-2 flex-wrap justify-content-center">
            <Button
              appearance="primary"
              size="sm"
              loading={uploadMutation.isPending}
              disabled={isBusy}
              onClick={(event) => { event.stopPropagation(); openFilePicker(); }}
            >
              <i className="bi bi-plus-lg me-2" />
              Add files
            </Button>
            {isMobile ? (
              <Button
                appearance="ghost"
                size="sm"
                disabled={isBusy}
                onClick={(event) => { event.stopPropagation(); openCameraPicker(); }}
              >
                <i className="bi bi-camera me-2" />
                Take photo
              </Button>
            ) : null}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ATTACHMENT_ACCEPT}
            disabled={isBusy}
            style={{ display: 'none' }}
            onChange={onUploadInputChange}
          />

          {isMobile ? (
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              disabled={isBusy}
              style={{ display: 'none' }}
              onChange={onUploadInputChange}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
